// Fee schedules for the three major handmade-friendly platforms — verified Apr 2026.
//
// Sources:
//   Etsy:           help.etsy.com/hc/en-us/articles/115014483627
//   Shopify Basic:  shopify.com/pricing  ($39/mo, 2.9% + 30¢ Shopify Payments)
//   Amazon Handmade: sell.amazon.com/programs/handmade (15% referral fee, $39.99/mo waived)
//
// Each platform exposes:
//   calculate({ price, shipping, cost, monthlyVolume }) -> {
//     perSaleFees, perSaleProfit, monthlyFixed, monthlyProfit,
//     effectiveFeeRatePct, breakdownLines: [{label, value}]
//   }
// All money in seller's currency (caller is responsible for conversion).

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

export const PLATFORMS = {
  etsy: {
    key: 'etsy',
    name: 'Etsy',
    icon: '🛍️',
    color: '#f1641e',
    accent: 'rgba(241, 100, 30, 0.05)',
    border: 'rgba(241, 100, 30, 0.4)',
    summary: 'Marketplace audience built-in. Best at low-medium volume.',
    pros: ['Built-in buyer traffic', 'Low fixed overhead', 'Easy setup'],
    cons: ['6.5% transaction fee', 'Possible Offsite Ads at 12-15%', 'Less brand control'],
    calculate: ({ price, shipping, cost, monthlyVolume, offsiteRate = 0 }) => {
      const p = num(price);
      const sh = num(shipping);
      const c = num(cost);
      const q = num(monthlyVolume);
      const revenuePerSale = p + sh;
      const revenue = revenuePerSale * q;

      const listingFee = 0.20 * q;
      const transactionFee = revenue * 0.065;
      const processingFee = revenue * 0.03 + 0.25 * q;
      const offsiteFee = offsiteRate > 0 ? Math.min(revenue * offsiteRate, 100 * q) : 0;
      const monthlyFees = listingFee + transactionFee + processingFee + offsiteFee;
      const monthlyCost = c * q;
      const monthlyProfit = revenue - monthlyFees - monthlyCost;
      const perSaleFees = q > 0 ? monthlyFees / q : 0;
      const perSaleProfit = q > 0 ? monthlyProfit / q : 0;
      const effectiveFeeRatePct = revenue > 0 ? (monthlyFees / revenue) * 100 : 0;

      return {
        perSaleFees,
        perSaleProfit,
        monthlyFixed: 0,
        monthlyFees,
        monthlyCost,
        monthlyRevenue: revenue,
        monthlyProfit,
        effectiveFeeRatePct,
        breakdownLines: [
          { label: 'Listing fee', value: listingFee },
          { label: 'Transaction (6.5%)', value: transactionFee },
          { label: 'Processing (3% + $0.25)', value: processingFee },
          ...(offsiteFee > 0 ? [{ label: `Offsite Ads (${(offsiteRate * 100).toFixed(0)}%)`, value: offsiteFee }] : []),
        ],
      };
    },
  },

  shopify: {
    key: 'shopify',
    name: 'Shopify Basic',
    icon: '🛒',
    color: '#22724a',
    accent: 'rgba(34, 114, 74, 0.05)',
    border: 'rgba(34, 114, 74, 0.4)',
    summary: 'Your own storefront. Pays off as volume grows.',
    pros: ['Lowest per-sale fees', 'Full brand & customer control', 'No marketplace competition'],
    cons: ['$39/mo subscription', 'You drive your own traffic', 'Apps & themes extra'],
    calculate: ({ price, shipping, cost, monthlyVolume }) => {
      const p = num(price);
      const sh = num(shipping);
      const c = num(cost);
      const q = num(monthlyVolume);
      const revenuePerSale = p + sh;
      const revenue = revenuePerSale * q;

      // Shopify Basic: $39/mo + 2.9% + 30¢ per online transaction (Shopify Payments)
      const monthlyPlanFee = 39;
      const processingFee = revenue * 0.029 + 0.30 * q;
      const monthlyFees = processingFee;
      const monthlyCost = c * q;
      const monthlyProfit = revenue - monthlyFees - monthlyCost - monthlyPlanFee;
      const perSaleFees = q > 0 ? (monthlyFees + monthlyPlanFee) / q : monthlyPlanFee;
      const perSaleProfit = q > 0 ? monthlyProfit / q : -monthlyPlanFee;
      const effectiveFeeRatePct = revenue > 0 ? ((monthlyFees + monthlyPlanFee) / revenue) * 100 : 0;

      return {
        perSaleFees,
        perSaleProfit,
        monthlyFixed: monthlyPlanFee,
        monthlyFees,
        monthlyCost,
        monthlyRevenue: revenue,
        monthlyProfit,
        effectiveFeeRatePct,
        breakdownLines: [
          { label: 'Plan: Basic ($39/mo)', value: monthlyPlanFee, isFixed: true },
          { label: 'Shopify Payments (2.9% + 30¢)', value: processingFee },
        ],
      };
    },
  },

  amazonHandmade: {
    key: 'amazonHandmade',
    name: 'Amazon Handmade',
    icon: '📦',
    color: '#ff9900',
    accent: 'rgba(255, 153, 0, 0.05)',
    border: 'rgba(255, 153, 0, 0.4)',
    summary: 'Massive Prime audience. Higher per-sale cut.',
    pros: ['250M+ Amazon shoppers', 'No monthly fee (waived)', 'Prime eligibility via FBA'],
    cons: ['15% referral on every sale', 'Strict approval process', 'Less artisan-buyer focus'],
    calculate: ({ price, shipping, cost, monthlyVolume }) => {
      const p = num(price);
      const sh = num(shipping);
      const c = num(cost);
      const q = num(monthlyVolume);
      const revenuePerSale = p + sh;
      const revenue = revenuePerSale * q;

      // Amazon Handmade: 15% referral fee on (price + shipping). No listing/sub fee.
      // (Jewelry has tiered rates, but we use the universal 15% as the headline rate.)
      const referralFee = revenue * 0.15;
      const monthlyFees = referralFee;
      const monthlyCost = c * q;
      const monthlyProfit = revenue - monthlyFees - monthlyCost;
      const perSaleFees = q > 0 ? monthlyFees / q : 0;
      const perSaleProfit = q > 0 ? monthlyProfit / q : 0;
      const effectiveFeeRatePct = revenue > 0 ? (monthlyFees / revenue) * 100 : 0;

      return {
        perSaleFees,
        perSaleProfit,
        monthlyFixed: 0,
        monthlyFees,
        monthlyCost,
        monthlyRevenue: revenue,
        monthlyProfit,
        effectiveFeeRatePct,
        breakdownLines: [
          { label: 'Referral fee (15%)', value: referralFee },
          { label: 'Pro plan ($39.99/mo)', value: 0, note: 'waived for Handmade' },
        ],
      };
    },
  },
};

export const PLATFORM_ORDER = ['etsy', 'shopify', 'amazonHandmade'];

/**
 * Find the volume at which the SECOND-best platform crosses below the best.
 * Used for showing break-even crossover advice ("At 50+ sales/mo, Shopify wins").
 */
export function findCrossoverVolume(price, shipping, cost, baseSettings = {}) {
  // Sweep from 1 to 200 sales/month and track which platform leads at each level
  const transitions = [];
  let prevWinner = null;
  for (let q = 1; q <= 200; q++) {
    const results = PLATFORM_ORDER.map(key => ({
      key,
      profit: PLATFORMS[key].calculate({ price, shipping, cost, monthlyVolume: q, ...baseSettings }).monthlyProfit,
    }));
    results.sort((a, b) => b.profit - a.profit);
    const winner = results[0].key;
    if (prevWinner !== null && winner !== prevWinner) {
      transitions.push({ atVolume: q, from: prevWinner, to: winner });
    }
    prevWinner = winner;
  }
  return transitions;
}
